import axios from '../axiosInstance';
import { InstallerFormData } from '../../containers/pages/CANsense/ManagementV2/dialogs/InstallerGeneratorDialog';
import { getApiBaseUrl, getForwardedHost } from './apiClient';

/**
 * CANsense Installer API Client
 * Handles installer generation via the backend API
 */

// ============================================================================
// Types
// ============================================================================

export interface ScrapeTarget {
  jobName: string;
  port: number;
  metricsPath?: string;
  scrapeInterval?: string;
}

export interface GenerateInstallerResponse {
  installer: {
    id: number;
    downloadUrl: string;
    filename: string;
    token: string;
    expiresAt: string;
    customerName: string;
    deviceName: string | null;
    os: 'windows' | 'linux';
    uses: number;
  };
}

// ============================================================================
// Helpers
// ============================================================================

const getApiUrl = (endpoint: string): string => {
  return `${getApiBaseUrl()}/sense/installers${endpoint}`;
};

// ============================================================================
// Installer Service
// ============================================================================

export class InstallerService {
  /**
   * Generate a new installer
   * POST /api3.2/sense/installers/generate
   */
  async generate(formData: InstallerFormData): Promise<GenerateInstallerResponse> {
    const API_URL = getApiUrl('/generate');
    const response = await axios.post(API_URL, {
      os: formData.osType,
      customer_id: formData.customerId,
      device_name: formData.deviceName || undefined,
      uses: formData.maxUses,
      expires_days: formData.expiryDays,
      scrape_targets: formData.scrapeTargets?.map(t => ({
        job_name: t.jobName,
        port: t.port,
        metrics_path: t.metricsPath,
        scrape_interval: t.scrapeInterval,
      })),
    }, {
      headers: {
        'X-Forwarded-Host': getForwardedHost(API_URL),
      },
    });
    const result = response.data?.data || response.data;
    // Normalize: if the response is the installer object directly (no wrapper),
    // wrap it so callers always get { installer: { ... } }
    if (result && !result.installer && result.token) {
      return { installer: result };
    }
    return result;
  }
}

// Export singleton instance
export const installerService = new InstallerService();
